/**
 * iter 264d.15 — CustomerFuzzyMatchDialog
 *
 * Shown when a user tries to create a customer whose name is >=85% similar
 * to an existing customer (e.g. "SteamLogistics" vs "Steam Logistics").
 * Lets them link to the existing entity instead of creating a duplicate.
 */
import React from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Link as LinkIcon, UserPlus } from 'lucide-react';

const CustomerFuzzyMatchDialog = ({
  open,
  onOpenChange,
  attemptedName,
  matches,
  onLink,
  onCreateAnyway,
  saving = false,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-900 border-slate-800 max-w-lg" data-testid="customer-fuzzy-match-dialog">
        <DialogHeader>
          <DialogTitle className="text-white flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-amber-400" />
            Possible duplicate customer
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            You&apos;re about to create <span className="font-mono text-white">&quot;{attemptedName}&quot;</span>, but
            these customers look like a match. Link to an existing customer so this shipment&apos;s
            history + billing stays in one place.
          </DialogDescription>
        </DialogHeader>

        <div className="mt-3 space-y-2 max-h-72 overflow-y-auto">
          {(matches || []).map((m) => {
            const pct = Math.round((m.similarity || 0) * 100);
            return (
              <button
                key={m.id}
                type="button"
                disabled={saving}
                onClick={() => onLink?.(m)}
                className="w-full text-left rounded-lg border border-slate-700 hover:border-blue-500 bg-slate-950 p-3 flex items-center justify-between transition-colors disabled:opacity-60"
                data-testid={`fuzzy-match-link-${m.id}`}
              >
                <div>
                  <div className="text-white font-medium">{m.name}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {m.code ? `Code ${m.code} · ` : ''}
                    {m.email || 'no email on file'}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    className={
                      pct >= 95
                        ? 'bg-emerald-500/20 text-emerald-300 border-emerald-500/40'
                        : pct >= 90
                          ? 'bg-blue-500/20 text-blue-300 border-blue-500/40'
                          : 'bg-amber-500/20 text-amber-300 border-amber-500/40'
                    }
                  >
                    {pct}% match
                  </Badge>
                  <LinkIcon className="h-4 w-4 text-blue-400" />
                </div>
              </button>
            );
          })}
        </div>

        <div className="mt-4 flex items-center justify-end gap-2 border-t border-slate-800 pt-3">
          <Button
            variant="outline"
            className="border-slate-700"
            disabled={saving}
            onClick={() => onOpenChange?.(false)}
            data-testid="fuzzy-match-cancel-btn"
          >
            Cancel
          </Button>
          <Button
            variant="outline"
            className="border-amber-500/40 text-amber-300 hover:bg-amber-500/10"
            disabled={saving}
            onClick={onCreateAnyway}
            data-testid="fuzzy-match-create-anyway-btn"
          >
            <UserPlus className="h-4 w-4 mr-2" />
            Create new anyway
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CustomerFuzzyMatchDialog;
